<template>
  <div class="anonymous-donation-form" ref="form">
    <!-- Payment frequency -->
    <div class="form-field">
      <h4 class="form-field-header" v-translate>Select payment frequency</h4>
      <div class="form-field-container">
        <CheckboxRadioButtons
          name="anonymous-donation-frequency-options"
          :options="Donation.options.FREQUENCY_CHOICES"
          :initial="defaultFrequencyOption"
          @selected="onChangeFrequency"
        />
      </div>
    </div>

    <!-- Amount -->
    <div class="form-field amount-field">
      <h4 class="form-field-header" v-translate>Amount</h4>
      <div class="form-field-container">
        <md-field class="amount-input form-field-text">
          <md-icon style="font-size: 18px !important">attach_money</md-icon>
          <md-input v-model="donation.amount" type="number" required></md-input>
        </md-field>
      </div>
    </div>

    <!-- Donor details -->
    <div class="form-field">
      <h3 class="donor-details-header" v-translate>Donor details</h3>
    </div>

    <div class="row-wrapper">
      <!-- First name -->
      <FormTextField label="First name" :value.sync="donor.firstName" />

      <!-- Last name -->
      <FormTextField label="Last name" :value.sync="donor.lastName" />
    </div>

    <div class="row-wrapper clear-margin">
      <!-- Email -->
      <FormTextField
        label="Email"
        :value.sync="donor.email"
        :showErrorMessage="hasSubmitted && emailInvalid"
        errorMessage="Please enter a valid email address."
        helpText="example@email.com"
      />

      <!-- Relationship to organization -->
      <div class="form-field">
        <h4 class="form-field-header" v-translate="{ orgName: organizationDetails.name }">
          Relationship to %{orgName}
        </h4>
        <div class="form-field-container">
          <select v-model="donor.orgRelationship">
            <option
              v-for="option in AnonymousDonor.options.ORG_RELATIONSHIP_CHOICES"
              :key="option.value"
              :value="option.value"
            >
              {{ optionLabel(option) }}
            </option>
          </select>
          <span class="form-dropdown" />
        </div>
      </div>
    </div>

    <div class="row-wrapper top-margin clear-margin">
      <!-- Unique ID -->
      <FormTextField
        v-if="isEmployee"
        :label="$gettext('Unique ID')"
        :labelTranslate="false"
        :value.sync="donor.employeeId"
        :helpText="organizationDetails.uniqueIdHelpCopy"
        :helpTextTranslate="false"
      />

      <!-- Honoree name -->
      <FormTextField
        v-else-if="isInMemoryOf"
        :label="$gettext('Name of honoree')"
        :labelTranslate="false"
        :value.sync="donor.otherRelationship"
      />

      <!-- Other -->
      <FormTextField
        v-else-if="isOther"
        :label="$gettext('Please specify')"
        :labelTranslate="false"
        :value.sync="donor.otherRelationship"
      />

      <!-- Zip code -->
      <FormTextField
        label="Zip Code"
        :value.sync="donor.zipCode"
        :showErrorMessage="hasSubmitted && !zipCodeValid"
        errorMessage="Please enter a valid, 5-digit zip code. If your zip code is less than five digits add leading zeros (e.g. 00123)"
        helpText="#####"
      />
    </div>

    <!-- Payment details -->
    <div class="form-field">
      <h3 class="payment-details-header" v-translate>Payment method</h3>
    </div>

    <!-- Card details -->
    <div class="form-field payment-field">
      <h4 class="form-field-header" v-translate>Card details</h4>
      <div class="form-field-container">
        <PaymentFormFlat @change="onPaymentUpdate" />
      </div>
    </div>

    <!-- Submit -->
    <div class="button-container">
      <LoadingSpinnerButton :loading="loading" :text="next" class="form-button" @click="onNext" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { stripe } from '@/services/stripe'
import LoadSpinner from '@/components/LoadSpinner'
import PaymentFormFlat from '@/components/donations/PaymentFormFlat'
import AnonymousDonor from '@/services/anonymousDonors'
import Donation from '@/services/donations'
import CheckboxRadioButtons from '@/components/CheckboxRadioButtons'
import LoadingSpinnerButton from '@/components/LoadingSpinnerButton'
import HelpIcon from '@/components/HelpIcon'
import User from '@/services/users'
import FormTextField from '@/components/forms/FormTextField'

export default {
  name: 'AnonymousDonationForm',
  components: {
    LoadSpinner,
    LoadingSpinnerButton,
    PaymentFormFlat,
    CheckboxRadioButtons,
    HelpIcon,
    FormTextField,
  },
  data() {
    return {
      AnonymousDonor,
      Donation,
      donor: AnonymousDonor.create({
        organization: this.$store.state.organizationDetails.id,
        orgRelationship: AnonymousDonor.options.EMPLOYEE,
      }),
      donation: Donation.create({
        amount: '',
        frequency: Donation.options.RECURRING,
        organization: this.$store.state.organizationDetails.id,
      }),
      loading: false,
      stripeCard: null,
      cardInfoComplete: false,
      showHelp: false,
      hasSubmitted: false,
    }
  },
  async created() {
    // If there's already a current donation in state, repopulate from there
    if (this.currentDonation) {
      this.donation = this.currentDonation
    }
    if (this.currentAnonymousDonor) {
      this.donor = this.currentAnonymousDonor
    }
  },
  methods: {
    clickedHelp() {
      // if showing, close and remove event listener
      // if not showing, open and add event listener
      if (!this.showHelp) {
        this.showHelp = true
        setTimeout(() => this.$refs.form.addEventListener('click', this.helpTextCallBack), 0)
      }
    },
    helpTextCallBack() {
      this.showHelp = false
      this.$refs.form.removeEventListener('click', this.helpTextCallBack)
    },
    onChangeFrequency(option) {
      this.donation.frequency = option.value
    },
    onPaymentUpdate(event) {
      this.stripeCard = event.stripeCard
      this.cardInfoComplete = event.complete
    },
    async onNext() {
      this.hasSubmitted = true
      this.loading = true
      // Format donation appropriately before saving to state
      const amount = Number(this.donation.amount).toFixed(2)
      const donation = this.donation
      this.donation.amount = amount
      this.$store.dispatch('updateCurrentDonation', this.donation)

      // Clear out irrelevant values before saving to state
      if (this.donor.orgRelationship !== AnonymousDonor.options.EMPLOYEE) {
        this.donor.employeeId = ''
      }

      this.$store.dispatch('updateCurrentAnonymousDonor', this.donor)

      // Token is passed in as param, not saved to state, to avoid storing sensitive data
      const response = await stripe.createToken(this.stripeCard)

      this.donation.paymentLast4 = response?.token?.card?.last4
      this.donation.paymentType =
        response?.token?.card?.funding === 'credit' ? 'Credit Card' : 'Debit Card'
      this.$store.dispatch('updateCurrentDonation', this.donation)

      this.$router.push({ name: 'DonationReview', params: { token: response.token.id } })

      this.loading = false
    },
    optionLabel(option) {
      if (option.value == AnonymousDonor.options.EMPLOYEE) {
        return (
          this.$options.filters.translateToCurrentLang(
            this.$language.current,
            this.organizationDetails.customVariables.cv3,
            this.organizationDetails.customVariables.cv3,
          ) || this.$gettext(option.label)
        )
      } else {
        return this.$gettext(option.label)
      }
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'currentDonation', 'currentAnonymousDonor']),
    /* Select frequency option to be selected by default. */
    defaultFrequencyOption() {
      return Donation.options.FREQUENCY_CHOICES.find(
        choice => choice.value === this.donation.frequency,
      )
    },
    /*
     * Disable donation if any relevant section is missing
     */
    disabled() {
      return (
        !this.donor.firstName ||
        !this.donor.lastName ||
        !this.donor.email ||
        !this.donor.orgRelationship ||
        !this.donor.zipCode ||
        !this.cardInfoComplete ||
        !this.donation.amount ||
        // Stripe doesn't accept donations less than 50 cents
        this.donation.amount < 0.5 ||
        !this.zipCodeValid ||
        this.emailInvalid
      )
    },
    zipCodeValid() {
      const zipRegex = new RegExp(/^[0-9]{5}?$/)

      if (this.donor.zipCode.length === 0) {
        return true
      }
      return (
        this.donor.zipCode &&
        this.donor.zipCode.length > 1 &&
        this.donor.zipCode.length === 5 &&
        !(this.errors && this.errors.zipCode) &&
        zipRegex.test(this.donor.zipCode)
      )
    },
    emailInvalid() {
      return this.donor.email && !User.validateEmail(this.donor.email)
    },
    next() {
      return this.$gettext('Next')
    },
    isEmployee() {
      return this.donor.orgRelationship === AnonymousDonor.options.EMPLOYEE
    },
    isInMemoryOf() {
      return this.donor.orgRelationship === AnonymousDonor.options.IN_HONOR_OR_MEMORY_OF
    },
    isOther() {
      return this.donor.orgRelationship === AnonymousDonor.options.OTHER
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/responsive';

.md-field .md-input {
  border-bottom: 0;
}

.row-wrapper {
  margin-top: 0;

  &.top-margin {
    margin-top: 1.5rem;
  }

  &.clear-margin {
    .form-field {
      margin-bottom: 0;
    }
  }
}

@include tablet-and-up-screen {
  .amount-field,
  .payment-field {
    max-width: 396px;
  }
}

.relationship {
  margin: 0;
  padding: 0;
}

.donor-details-header,
.payment-details-header {
  color: black;
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 2rem;
  margin-top: 2.5rem;
}

.amount-input {
  padding: 0.75rem;
}
</style>
